import classNames from 'classnames'
import { useState, useRef, useEffect } from 'react'
import { Input, InputProps } from 'reactstrap'
import { useOutsideClick } from 'web/hooks/useOutsideClick'
import { isNonEmptyString, updateIndexCircular } from 'web/utils/utils'
import { DivBtn } from './DivBtn'
import { SvgCross } from './SvgCross'

type SuggestboxInputProps = {
    suggestbox: {
        options: string[]
        onChange: (event: React.ChangeEvent<HTMLInputElement>) => void
    }
} & InputProps

/**
 * This was made extremely quick and dirty. Started as a typeahead
 * input, but a suggestbox feature was enough.
 */
export const SuggestBoxInput = (props: SuggestboxInputProps): JSX.Element | null => {

    const [show, setShow] = useState(false)
    const dropdownRef = useRef<HTMLInputElement | null>(null)
    const selectedRef = useRef<HTMLDivElement | null>(null)

    const [selectedIndex, setSelectedIndex] = useState<number | null>(null)

    useOutsideClick({
        ref: dropdownRef,
        onOutsideClick() {
            setShow(false)
        },
    })

    useEffect(() => {
        selectedRef.current?.scrollIntoView({
            behavior: "auto",
            block: "nearest",
            inline: "nearest"
        })
    }, [selectedRef, selectedIndex])

    const suggestions = props.suggestbox.options.filter(opt => {
        if (props.value != null) {
            return opt.toLowerCase().startsWith((props.value as string).toLowerCase())
        }

        return true
    })

    useEffect(() => {
        setSelectedIndex(null)
    }, [suggestions.length])

    return (
        <div
            ref={dropdownRef}
            style={{ position: 'relative' }}
        >
            <Input
                {...props}

                onChange={(e) => {
                    props.suggestbox.onChange(e)
                    setShow(true)
                }}

                onKeyDownCapture={(e) => {
                    if (e.key === 'Tab') {
                        setShow(false)
                    }

                    if (e.key === 'ArrowUp') {
                        setSelectedIndex(updateIndexCircular(selectedIndex, 0, suggestions.length - 1, 'up'))
                        e.preventDefault()
                    } else if (e.key === 'ArrowDown') {
                        setSelectedIndex(updateIndexCircular(selectedIndex, 0, suggestions.length - 1, 'down'))
                        e.preventDefault()
                    }

                    if (e.key === 'Enter') {
                        e.preventDefault()

                        if (selectedIndex == null) {
                            return
                        }

                        setShow(false)
                        setSelectedIndex(null)

                        const eventOptions: any = {
                            bubbles: true,
                            cancelable: true,
                            composed: false,
                            target: {
                                value: suggestions[selectedIndex]
                            }
                        }

                        props.suggestbox.onChange(eventOptions as any)
                    }

                }}

                onFocus={() => {
                    if (props.value != null && (props.value as string).length <= 0) {
                        setShow(true)
                    }
                }}
            />

            {isNonEmptyString(props.value) && (
                <div
                    className="d-flex"
                    style={{
                        position: 'absolute',
                        right: 0,
                        top: 0,
                        bottom: 0,
                    }}
                >
                    <DivBtn
                        disableTab
                        fillHeight
                        fillWidth
                        onClick={() => {
                            const eventOptions: any = {
                                bubbles: true,
                                cancelable: true,
                                composed: false,
                                target: {
                                    value: ''
                                }
                            }

                            props.suggestbox.onChange(eventOptions as any)
                        }}
                    >
                        <div className="align-self-stretch align-items-center d-flex px-2">
                            <SvgCross />
                        </div>
                    </DivBtn>
                </div>
            )}

            {(show && suggestions.length >= 1) && (
                <div
                    tabIndex={-1}
                    className="rounded small bg-white border mt-2 w-100 shadow cursor-pointer"
                    style={{
                        position: 'absolute',
                        zIndex: 1,
                        maxHeight: 200,
                        overflowY: 'auto',
                        scrollbarColor: 'silver whitesmoke',
                        scrollbarWidth: 'thin',
                    }}
                >
                    {suggestions.length >= 1 && suggestions.map((opt, index) => (
                        <div
                            ref={selectedIndex === index ? selectedRef : undefined}
                            key={index}
                            onClick={() => {
                                setShow(false)

                                const eventOptions: any = {
                                    bubbles: true,
                                    cancelable: true,
                                    composed: false,
                                    target: {
                                        value: opt
                                    }
                                }

                                props.suggestbox.onChange(eventOptions as any)
                            }}
                            className={classNames(
                                'px-2 py-2',
                                { 'bg-primary text-white': selectedIndex === index },
                                { 'bg-light-hover': selectedIndex !== index }
                            )}
                        >
                            {opt}
                        </div>
                    ))}
                </div>
            )
            }
        </div >
    )
}
